import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { APIRequestStatus } from "common-ts/dist/models/APIRequestStatus";

// Uniform-line (made-to-order) order stream. Like accounting, these actions are temporarily routed
// through the livestream endpoint with a { accountingAction, payload } body (until dedicated
// endpoints are launched). Uniform orders live in the same OrderTable as online orders but use
// orderType "UNIFORM_ORDER", so the two business lines never appear in each other's views.
const UNIFORM_ENDPOINT =
    "https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/livestream-3820238";

async function callUniform(accountingAction: string, payload: any = {}) {
    try {
        const response = await axios({
            method: "post",
            url: UNIFORM_ENDPOINT,
            data: JSON.stringify({ accountingAction, payload }),
        });
        return response.data;
    } catch (error: any) {
        if (error?.response?.data) {
            return error.response.data;
        }
        throw error;
    }
}

const initialState: any = {
    uniformOrdersList: [],
    listStatus: APIRequestStatus.Idle,
    postStatus: APIRequestStatus.Idle,
    lastResult: null,
};

export const fetchUniformOrders = createAsyncThunk("uniformOrders/fetch", async () => {
    const data = await callUniform("UNIFORM_ORDERS_GET");
    return data;
});

export const createUniformOrder = createAsyncThunk("uniformOrders/create", async (arg: any, thunkAPI) => {
    const data = await callUniform("UNIFORM_ORDER_POST", arg);
    thunkAPI.dispatch(fetchUniformOrders());
    return data;
});

export const editUniformOrder = createAsyncThunk("uniformOrders/edit", async (arg: any, thunkAPI) => {
    const data = await callUniform("UNIFORM_ORDER_POST", arg);
    thunkAPI.dispatch(fetchUniformOrders());
    return data;
});

export const addUniformJobCost = createAsyncThunk("uniformOrders/jobCost", async (arg: any, thunkAPI) => {
    const data = await callUniform("UNIFORM_JOB_COST_POST", arg);
    thunkAPI.dispatch(fetchUniformOrders());
    return data;
});

export const recordUniformPayment = createAsyncThunk("uniformOrders/payment", async (arg: any, thunkAPI) => {
    const data = await callUniform("UNIFORM_PAYMENT_POST", arg);
    thunkAPI.dispatch(fetchUniformOrders());
    return data;
});

export const shipUniformOrder = createAsyncThunk("uniformOrders/ship", async (arg: any, thunkAPI) => {
    const data = await callUniform("UNIFORM_ORDER_SHIP", arg);
    thunkAPI.dispatch(fetchUniformOrders());
    return data;
});

// Converts an unpaid online order (orderType "ORDER") into a uniform order. arg: { orderId } or
// { userSub, creationDateTime }.
export const convertOnlineOrder = createAsyncThunk("uniformOrders/convert", async (arg: any, thunkAPI) => {
    const data = await callUniform("UNIFORM_CONVERT", arg);
    thunkAPI.dispatch(fetchUniformOrders());
    return data;
});

export const uniformOrderSlice = createSlice({
    name: "uniformOrders",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(fetchUniformOrders.pending, (state) => {
                state.listStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchUniformOrders.fulfilled, (state, action) => {
                state.uniformOrdersList = action.payload?.uniformOrdersList ?? [];
                state.listStatus = APIRequestStatus.Success;
            })
            .addCase(fetchUniformOrders.rejected, (state) => {
                state.listStatus = APIRequestStatus.Failure;
            })
            .addCase(createUniformOrder.pending, (state) => {
                state.postStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(createUniformOrder.fulfilled, (state, action) => {
                state.postStatus = APIRequestStatus.Success;
                state.lastResult = action.payload;
            })
            .addCase(createUniformOrder.rejected, (state) => {
                state.postStatus = APIRequestStatus.Failure;
            });
    },
});

export default uniformOrderSlice.reducer;
